import { BulkActionProgress } from "../types";

type BulkActionContext = any;

export type BulkActionOnProgress = (progress: BulkActionProgress) => void;

export function bulkActionFactory<T = any>({
  action,
  onStart,
  onFinish,
  onProgress,
  getSuccessDescription,
  getFailedDescription,
  getProgressMessage,
  concurrentLimit = 16
}: {
  action: (item: T, context?: BulkActionContext) => Promise<any>;
  onStart?: () => void;
  onFinish?: (result: { successItems: T[]; failedItems: T[] }) => void;
  onProgress?: BulkActionOnProgress;
  getProgressMessage?: (item: T, progress: BulkActionProgress) => string;
  getSuccessDescription?: (item: T) => string;
  getFailedDescription?: (item: T) => string;
  concurrentLimit?: number;
}) {
  const run = async (
    items: T[],
    context?: BulkActionContext
  ): Promise<{
    successItems: T[];
    failedItems: T[];
  }> => {
    await onStart?.();

    const successItems: T[] = [];
    const failedItems: T[] = [];
    const successDescriptions: string[] = [];
    const failedDescriptions: string[] = [];

    const requests = [...items];
    const total = items.length;

    async function next(...batchItems: T[]) {
      await Promise.all(
        batchItems.map(async item => {
          const beforeActionProgressProps: BulkActionProgress = {
            total,
            processed: successItems.length + failedItems.length,
            successful: successItems.length,
            failed: failedItems.length,
            successDescriptions: [...successDescriptions],
            failedDescriptions: [...failedDescriptions]
          };

          onProgress?.({
            ...beforeActionProgressProps,
            progressMessage: getProgressMessage
              ? getProgressMessage(item, beforeActionProgressProps)
              : undefined
          });

          try {
            await action(item, context);
            successItems.push(item);
            const desc = getSuccessDescription?.(item);
            if (desc) {
              successDescriptions.push(desc);
            }
          } catch {
            failedItems.push(item);
            const desc = getFailedDescription?.(item);
            if (desc) {
              failedDescriptions.push(desc);
            }
          }

          const afterActionProgressProps: BulkActionProgress = {
            total,
            processed: successItems.length + failedItems.length,
            successful: successItems.length,
            failed: failedItems.length,
            successDescriptions: [...successDescriptions],
            failedDescriptions: [...failedDescriptions]
          };

          onProgress?.({
            ...afterActionProgressProps,
            progressMessage: getProgressMessage
              ? getProgressMessage(item, afterActionProgressProps)
              : undefined
          });

          const nextItem = requests.pop();

          if (nextItem) {
            await next(nextItem);
          }
        })
      );
    }

    await next(...requests.splice(0, concurrentLimit));

    await onFinish?.({
      successItems,
      failedItems
    });

    return {
      successItems,
      failedItems
    };
  };

  return {
    run
  };
}
